import { Box, Grid, useTheme } from "@mui/material";
import { durationDisplay } from "components/SoundClipEditor/Controls/Player";
import { observer } from "mobx-react";
import moment from "moment-timezone";
import React from "react";
import {
    ConversationClassifier,
    Highlight,
} from "services/ConversationService";
import clsx from "clsx";
import ConversationClassifierChipList from "../../Drawer/components/ConversationClassifierChipList/ConversationClassifierChipList";
import ConversationHighlights from "../Highlights/ConversationHighlights";
import { AcxExpandableText } from "components/UI/AcxExpandableText";
import { applyTimestampHeuristic } from "components/UI/AcxDataGrid/ColumnTypes/DateColTypes";

type Props = {
    summary: string;
    callDurationMillis: number;
    timestamp: string;
    agentFullName: string;
    conversationClassifiers: ConversationClassifier[];
    highlights: Highlight;
    isChat: boolean;
    selectedClassifierIds?: string[];
    hasTranslation?: boolean;
    nonEnglish?: boolean;
    chatMessageCount?: number;
};

const ConversationSummary: React.FunctionComponent<Props> = observer(
    (props) => {
        const theme = useTheme();

        const rbcClassifierIds =
            props.highlights?.rbcHighlightResults.map(
                (rbc) => rbc.classifierId,
            ) ?? [];

        const filteredConversationClassifiers =
            props.conversationClassifiers.filter(
                (rbc) => !rbcClassifierIds.includes(rbc.classifierId),
            );

        const isTranslated = props.hasTranslation ?? false;
        const nonEnglish = props.nonEnglish ?? false;
        const formattedTimestamp = moment(
            applyTimestampHeuristic(props.timestamp),
        )
            .tz(moment.tz.guess())
            .format("MM/DD/YYYY h:mm:ss A z");

        return (
            <Grid sx={{ color: theme.palette.black.main, p: 1 }}>
                <Grid item sx={{ color: theme.palette.gray.main, p: 1 }}>
                    <Box
                        component="span"
                        fontWeight="bold"
                        sx={{ color: "#2C3F92", pr: "10px" }}
                    >
                        {props.agentFullName}
                    </Box>
                    {props.isChat && props.chatMessageCount
                        ? `${props.chatMessageCount} ${
                              props.chatMessageCount === 1
                                  ? "Message"
                                  : "Messages"
                          }`
                        : durationDisplay(props.callDurationMillis / 1000)}
                    <span style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                        |
                    </span>
                    {formattedTimestamp}
                    {isTranslated && (
                        <>
                            <span
                                style={{
                                    paddingLeft: "4px",
                                    paddingRight: "4px",
                                }}
                            >
                                |
                            </span>
                            <span
                                style={{
                                    color: "#9CA3AF",
                                    fontStyle: "italic",
                                    fontWeight: "bold",
                                    fontSize: "13px",
                                }}
                            >
                                Translated
                            </span>
                        </>
                    )}
                    {!isTranslated && nonEnglish && (
                        <>
                            <span
                                style={{
                                    paddingLeft: "4px",
                                    paddingRight: "4px",
                                }}
                            >
                                |
                            </span>
                            <span
                                style={{
                                    color: "#9CA3AF",
                                    fontStyle: "italic",
                                    fontWeight: "bold",
                                    fontSize: "13px",
                                }}
                            >
                                Non-English
                            </span>
                        </>
                    )}
                </Grid>

                <Grid item className={clsx("pendo-ignore")} sx={{ p: 1 }}>
                    <AcxExpandableText
                        pendoIgnore
                        data-testid="conversation-summary-expandable-text"
                        maxTruncatedLines={4}
                        containerSx={{ marginBottom: 1 }}
                    >
                        <Box fontWeight="bold">Conversation Summary:</Box>{" "}
                        {!props.summary ? "N/A" : props.summary}
                    </AcxExpandableText>
                    {props.highlights && (
                        <ConversationHighlights
                            highlights={props.highlights}
                            conversationClassifiers={
                                props.conversationClassifiers
                            }
                            isChat={props.isChat}
                        />
                    )}
                </Grid>
                <Grid item sx={{ p: 1 }}>
                    <ConversationClassifierChipList
                        conversationClassifiers={
                            filteredConversationClassifiers
                        }
                        selectedClassifierIds={props.selectedClassifierIds}
                    />
                </Grid>
            </Grid>
        );
    },
);

export default ConversationSummary;
